// PATH: src/components/documents/ChapterImportDialog.tsx
import * as React from "react";
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DataObjectIcon from "@mui/icons-material/DataObject";

import type { CurriculumProgram, Document } from "../../models/domain";
import { useChaptersApi, type ChapterImportResponse } from "../../api/chapters/chapters";
import { useObjectivesApi, type ObjectivesResponse } from "../../api/objectives/objectives";
import { errorMessage, TemplateDownload } from "../ui";
import {
  countLessonHeadings,
  formatChapterImportError,
  formatChapterImportResult,
  parseManifestPreview,
  type ManifestPreviewResult,
} from "./chapterImport";
import { formatLessonsRun } from "./lessonCheck";
import LessonsRunAlert from "./LessonsRunAlert";

interface Props {
  open: boolean;
  onClose: () => void;
  /** Import réussi : la liste des chapitres doit être rechargée. */
  onImported?: (doc: Document) => void;
  /** Ouvre le panneau de contrôle du chapitre importé. */
  onOpenChapter?: (doc: Document) => void;
  /** Chapitres déjà chargés : pour annoncer « nouvelle version de … » (indicatif). */
  knownDocuments?: Document[];
  /** Catalogue : pour montrer les noms en face des codes du manifeste. */
  programs?: CurriculumProgram[];
}

const STEPS = ["Fichiers", "Aperçu", "Résultat"];

const formatSize = (bytes: number) =>
  bytes < 1024
    ? `${bytes} o`
    : bytes < 1024 * 1024
      ? `${(bytes / 1024).toFixed(1)} Ko`
      : `${(bytes / 1024 / 1024).toFixed(1)} Mo`;

interface DropZoneProps {
  label: string;
  hint: string;
  accept: string;
  icon: React.ReactNode;
  file: File | null;
  detail?: string | null;
  error?: string | null;
  onFile: (file: File | null) => void;
}

/** Zone de dépôt : clic ou glisser-déposer d'un fichier. */
const DropZone: React.FC<DropZoneProps> = ({ label, hint, accept, icon, file, detail, error, onFile }) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [over, setOver] = React.useState(false);
  const ok = Boolean(file) && !error;

  return (
    <ButtonBase
      component="div"
      role="button"
      tabIndex={0}
      onClick={() => inputRef.current?.click()}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") inputRef.current?.click();
      }}
      onDragOver={(e: React.DragEvent) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e: React.DragEvent) => {
        e.preventDefault();
        setOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f) onFile(f);
      }}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        width: "100%",
        p: 2,
        borderRadius: 3,
        border: "1.5px dashed",
        borderColor: error ? "error.main" : over ? "primary.main" : ok ? "success.main" : "divider",
        bgcolor: over ? "action.selected" : ok ? "background.default" : "background.paper",
        textAlign: "start",
        transition: "border-color .15s, background-color .15s",
        "&:hover": { borderColor: error ? "error.main" : "primary.main" },
      }}
    >
      <input
        ref={inputRef}
        type="file"
        hidden
        accept={accept}
        onChange={(e) => {
          onFile(e.target.files?.[0] ?? null);
          e.target.value = "";
        }}
      />
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box sx={{ color: ok ? "success.main" : error ? "error.main" : "text.secondary", display: "flex" }}>
          {ok ? <CheckCircleIcon /> : icon}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle2">{label}</Typography>
          {file ? (
            <Typography variant="body2" color="text.secondary" noWrap title={file.name}>
              {file.name} · {formatSize(file.size)}
              {detail ? ` · ${detail}` : ""}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {hint}
            </Typography>
          )}
        </Box>
        <Typography variant="body2" color="primary" fontWeight={600} sx={{ flexShrink: 0 }}>
          {file ? "Changer" : "Choisir"}
        </Typography>
      </Stack>
      {error && (
        <Typography variant="body2" color="error.dark" sx={{ mt: 1 }}>
          {error}
        </Typography>
      )}
    </ButtonBase>
  );
};

const Field: React.FC<{ label: string; value: React.ReactNode; code?: string }> = ({ label, value, code }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body1" dir="auto" sx={{ wordBreak: "break-word" }}>
      {value || "—"}
    </Typography>
    {code && (
      <Typography variant="caption" className="mono" color="text.secondary">
        {code}
      </Typography>
    )}
  </Box>
);

/**
 * Import d'un chapitre en trois étapes : les deux fichiers (le Markdown, une
 * `#` par leçon, et son manifeste JSON), l'aperçu du manifeste lu côté client
 * (le serveur valide et fait foi), puis le résultat, d'où l'on lance
 * l'extraction des objectifs.
 */
export const ChapterImportDialog: React.FC<Props> = ({
  open,
  onClose,
  onImported,
  onOpenChapter,
  knownDocuments = [],
  programs = [],
}) => {
  const { importChapter } = useChaptersApi();
  const { extractObjectives } = useObjectivesApi();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [step, setStep] = React.useState(0);
  const [markdown, setMarkdown] = React.useState<File | null>(null);
  const [headings, setHeadings] = React.useState<number | null>(null);
  const [manifest, setManifest] = React.useState<File | null>(null);
  const [preview, setPreview] = React.useState<ManifestPreviewResult | null>(null);
  const [isActive, setIsActive] = React.useState(true);

  const [importing, setImporting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [result, setResult] = React.useState<ChapterImportResponse | null>(null);

  const [extracting, setExtracting] = React.useState(false);
  const [extractError, setExtractError] = React.useState<string | null>(null);
  const [extraction, setExtraction] = React.useState<ObjectivesResponse | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setStep(0);
    setMarkdown(null);
    setHeadings(null);
    setManifest(null);
    setPreview(null);
    setIsActive(true);
    setImporting(false);
    setError(null);
    setResult(null);
    setExtracting(false);
    setExtractError(null);
    setExtraction(null);
  }, [open]);

  const onChooseMarkdown = async (f: File | null) => {
    setMarkdown(f);
    setHeadings(null);
    if (!f) return;
    try {
      setHeadings(countLessonHeadings(await f.text()));
    } catch (e) {
      console.error(e);
    }
  };

  const onChooseManifest = async (f: File | null) => {
    setManifest(f);
    setPreview(null);
    if (!f) return;
    try {
      setPreview(parseManifestPreview(await f.text()));
    } catch (e) {
      setPreview({ ok: false, error: `Lecture du manifeste impossible : ${String(e)}` });
    }
  };

  const p = preview?.ok ? preview.preview : null;
  const existing = p?.chapterRef ? knownDocuments.find((d) => d.chapter_ref === p.chapterRef) : undefined;
  const headingMismatch = p != null && headings != null && headings !== p.lessons.length;

  /* Noms du catalogue en face des codes du manifeste, quand on les trouve ;
     sinon le code seul (le serveur tranche). */
  const names = React.useMemo(() => {
    if (!p) return null;
    const prog = programs.find((x) => x.program.code === p.program);
    let grade: string | undefined;
    let track: string | undefined;
    prog?.stages.forEach((st) =>
      st.grades.forEach((g) => {
        if (g.grade.code === p.grade) {
          grade = g.grade.name;
          track = g.tracks?.find((t) => t.code === p.track)?.name;
        }
      }),
    );
    const subject = prog?.subjects.find((s) => s.code === p.subject)?.name;
    return { program: prog?.program.name, grade, subject, track };
  }, [p, programs]);

  const canPreview = Boolean(markdown && manifest && preview?.ok);

  const handleImport = async () => {
    if (!markdown || !manifest) return;
    setImporting(true);
    setError(null);
    try {
      const { data } = await importChapter({ file: markdown, manifest, isActive });
      setResult(data);
      setStep(2);
      onImported?.(data.document);
    } catch (e) {
      setError(formatChapterImportError(e));
    } finally {
      setImporting(false);
    }
  };

  const handleExtract = async () => {
    if (!result?.document?.id) return;
    setExtracting(true);
    setExtractError(null);
    try {
      const { data } = await extractObjectives(result.document.id);
      setExtraction(data);
      onImported?.(result.document);
    } catch (e) {
      setExtractError(errorMessage(e, "L'extraction des objectifs a échoué."));
    } finally {
      setExtracting(false);
    }
  };

  const lessonsRun = extraction?.lessons ? formatLessonsRun(extraction.lessons, extraction) : null;

  return (
    <Dialog open={open} onClose={importing ? undefined : onClose} maxWidth="md" fullWidth fullScreen={fullScreen}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ flex: 1 }}>Importer un chapitre</Box>
        <Tooltip title="Fermer">
          <span>
            <IconButton onClick={onClose} disabled={importing} aria-label="Fermer" size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </DialogTitle>
      <DialogContent>
        <Stepper activeStep={step} alternativeLabel sx={{ mb: 3, mt: 1 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {step === 0 && (
          <Stack spacing={2}>
            <Typography variant="body1" color="text.secondary">
              Le Markdown ouvre une leçon par ligne <code># Titre</code>, dans l'ordre des leçons du manifeste. Un
              chapitre déjà importé (même référence) reçoit une nouvelle version.
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: "background.default" }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
              >
                <Box>
                  <Typography variant="subtitle2">Partir d'un modèle</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Un chapitre d'exemple complet (2 leçons, 4 notions), accepté tel quel par l'import.
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <TemplateDownload file="chapitre-exemple.md">Markdown</TemplateDownload>
                  <TemplateDownload file="chapitre-exemple.manifest.json">Manifeste</TemplateDownload>
                </Stack>
              </Stack>
              <Box
                component="ul"
                sx={{ m: 0, mt: 1.5, pl: 2.5, color: "text.secondary", "& li": { fontSize: "0.8125rem", mb: 0.25 } }}
              >
                <li>
                  <code># Titre</code> ouvre une leçon : autant de lignes <code>#</code> que de leçons dans{" "}
                  <code>lecons</code>, dans le même ordre.
                </li>
                <li>
                  <code>## Titre</code> ouvre une notion : chaque notion devient un passage du cours.
                </li>
                <li>
                  Manifeste : <code>chapter_ref</code>, <code>program</code>, <code>grade</code>, <code>subject</code>,{" "}
                  <code>language</code>, <code>titre</code> et au moins une leçon sont obligatoires ; les codes sont
                  ceux du Catalogue ; chaque <code>ossature_ref</code> est unique.
                </li>
                <li>
                  Chaque leçon de <code>lecons</code> donne <code>ossature_ref</code>, <code>ordre</code> et{" "}
                  <code>titre</code>, sans objectifs : ils sont déduits du cours, leçon par leçon, à l'extraction.
                </li>
              </Box>
            </Paper>
            <DropZone
              label="Document Markdown"
              hint="Glissez le fichier .md ici, ou cliquez pour le choisir"
              accept=".md,.markdown,text/markdown,text/plain"
              icon={<DescriptionOutlinedIcon />}
              file={markdown}
              detail={headings != null ? `${headings} titre(s) de leçon` : null}
              onFile={(f) => void onChooseMarkdown(f)}
            />
            <DropZone
              label="Manifeste JSON"
              hint="Glissez le fichier .json ici, ou cliquez pour le choisir"
              accept=".json,application/json"
              icon={<DataObjectIcon />}
              file={manifest}
              detail={p ? `${p.lessons.length} leçon(s)` : null}
              error={preview && !preview.ok ? preview.error : null}
              onFile={(f) => void onChooseManifest(f)}
            />
          </Stack>
        )}

        {step === 1 && p && (
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              {p.titre && (
                <Typography variant="h5" dir="auto" sx={{ mb: 0.5 }}>
                  {p.titre}
                </Typography>
              )}
              <Typography variant="body2" className="mono" color="text.secondary" sx={{ mb: 2 }}>
                {p.chapterRef || "référence absente"}
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" },
                  gap: 2,
                }}
              >
                <Field
                  label="Programme"
                  value={names?.program ?? p.program}
                  code={names?.program ? p.program : undefined}
                />
                <Field label="Niveau" value={names?.grade ?? p.grade} code={names?.grade ? p.grade : undefined} />
                <Field
                  label="Matière"
                  value={names?.subject ?? p.subject}
                  code={names?.subject ? p.subject : undefined}
                />
                <Field
                  label="Filière"
                  value={p.track ? (names?.track ?? p.track) : "Commun"}
                  code={p.track && names?.track ? p.track : undefined}
                />
                <Field label="Semestre" value={p.semester != null ? `S${p.semester}` : "—"} />
                <Field label="Ordre" value={p.chapterOrder != null ? String(p.chapterOrder) : "—"} />
                <Field label="Langue" value={p.language} />
                <Field label="Leçons" value={String(p.lessons.length)} />
              </Box>
            </Paper>

            {p.missing.length > 0 && (
              <Alert severity="error">
                Champ(s) absent(s) ou vide(s) dans le manifeste : <span className="mono">{p.missing.join(", ")}</span>.
                Le serveur refusera l'import.
              </Alert>
            )}
            {headingMismatch && (
              <Alert severity="warning">
                Le Markdown a {headings} ligne(s) <code>#</code> pour {p.lessons.length} leçon(s) au manifeste : il en
                faut exactement autant (le serveur tranche).
              </Alert>
            )}
            {existing && (
              <Alert severity="info">
                Déjà importé : « {existing.title} ». L'import créera une nouvelle version de ce chapitre.
              </Alert>
            )}

            <TableContainer component={Paper} variant="outlined">
              <Table size="small" sx={{ minWidth: 480 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 56 }}>Ordre</TableCell>
                    <TableCell>Leçon</TableCell>
                    <TableCell>Référence</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {p.lessons.map((l, i) => (
                    <TableRow key={`${l.ossatureRef}-${i}`}>
                      <TableCell className="mono">{l.ordre ?? i + 1}</TableCell>
                      <TableCell dir="auto">{l.titre || "—"}</TableCell>
                      <TableCell>
                        <Typography variant="caption" className="mono" color="text.secondary">
                          {l.ossatureRef || "—"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={3} sx={{ fontWeight: 600, color: "text.primary" }}>
                      {p.lessons.length} leçon(s)
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>

            {p.ignoredObjectives > 0 && (
              <Typography variant="caption" color="text.secondary">
                {p.ignoredObjectives} objectif(s) du manifeste ignoré(s) : les objectifs sont déduits du cours.
              </Typography>
            )}

            <FormControlLabel
              sx={{ m: 0 }}
              control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} sx={{ mr: 1.5 }} />}
              // tc_embed_be n'applique l'état qu'à un chapitre NOUVEAU : une nouvelle
              // version garde l'interrupteur posé dans la liste des chapitres.
              label="Actif : proposé aux élèves dès l'import (nouveau chapitre ; une nouvelle version garde son état)"
            />

            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        )}

        {step === 2 && result && (
          <Stack spacing={2}>
            <Alert severity="success">{formatChapterImportResult(result)}</Alert>

            <Paper variant="outlined" sx={{ p: 2.5 }}>
              <Typography variant="subtitle1">Étape suivante : extraire les objectifs</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                L'extraction lit le chapitre leçon par leçon et en déduit les objectifs de chaque leçon. Sans elle, le
                chapitre n'est pas jouable.
              </Typography>
              {extraction ? (
                lessonsRun ? (
                  <LessonsRunAlert view={lessonsRun} />
                ) : (
                  <Alert severity="warning">
                    {extraction.objectives.length} objectif(s) extrait(s). Pas de bilan par leçon : le serveur ne voit
                    pas de manifeste pour ce document.
                  </Alert>
                )
              ) : (
                <Button
                  variant="contained"
                  onClick={() => void handleExtract()}
                  disabled={extracting}
                  startIcon={extracting ? <CircularProgress size={16} color="inherit" /> : null}
                >
                  {extracting ? "Extraction en cours…" : "Extraire les objectifs"}
                </Button>
              )}
              {extractError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {extractError}
                </Alert>
              )}
            </Paper>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {step === 0 && (
          <>
            <Button color="inherit" onClick={onClose}>
              Annuler
            </Button>
            <Button variant="contained" onClick={() => setStep(1)} disabled={!canPreview}>
              Continuer
            </Button>
          </>
        )}
        {step === 1 && (
          <>
            <Button color="inherit" onClick={() => setStep(0)} disabled={importing}>
              Retour
            </Button>
            <Button
              variant="contained"
              onClick={() => void handleImport()}
              disabled={importing || !markdown || !manifest}
              startIcon={importing ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {importing ? "Import…" : "Importer"}
            </Button>
          </>
        )}
        {step === 2 && (
          <>
            {onOpenChapter && result && (
              <Button color="inherit" onClick={() => onOpenChapter(result.document)} disabled={extracting}>
                Contrôler le chapitre
              </Button>
            )}
            <Button variant={extraction ? "contained" : "outlined"} onClick={onClose} disabled={extracting}>
              Terminer
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ChapterImportDialog;
